import { createContext, useCallback, useContext, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { registerApiErrorHandler } from '../services/api';
import * as styles from '../components/shared/Toast.css.ts';

export type ToastVariant = 'error' | 'success' | 'info';

interface ToastItem {
  id: number;
  message: string;
  variant: ToastVariant;
}

interface ToastApi {
  error: (message: string) => void;
  success: (message: string) => void;
  info: (message: string) => void;
}

const ToastContext = createContext<ToastApi | undefined>(undefined);

const DISMISS_MS: Record<ToastVariant, number> = {
  error: 8000,
  success: 4000,
  info: 4000,
};

let nextId = 1;

export function ToastProvider({ children }: { children: ReactNode }) {
  const [toasts, setToasts] = useState<ToastItem[]>([]);
  const timersRef = useRef(new Map<number, ReturnType<typeof setTimeout>>());

  const dismiss = useCallback((id: number) => {
    const timer = timersRef.current.get(id);
    if (timer) {
      clearTimeout(timer);
      timersRef.current.delete(id);
    }
    setToasts((current) => current.filter((toast) => toast.id !== id));
  }, []);

  const push = useCallback(
    (message: string, variant: ToastVariant) => {
      const id = nextId++;
      setToasts((current) => {
        // Dedupe: don't stack identical messages (e.g. retry loops)
        if (current.some((toast) => toast.message === message && toast.variant === variant)) {
          return current;
        }
        timersRef.current.set(
          id,
          setTimeout(() => dismiss(id), DISMISS_MS[variant])
        );
        return [...current, { id, message, variant }];
      });
    },
    [dismiss]
  );

  const api = useMemo<ToastApi>(
    () => ({
      error: (message: string) => push(message, 'error'),
      success: (message: string) => push(message, 'success'),
      info: (message: string) => push(message, 'info'),
    }),
    [push]
  );

  useEffect(() => {
    registerApiErrorHandler((message) => push(message, 'error'));
    const timers = timersRef.current;
    return () => {
      registerApiErrorHandler(null);
      timers.forEach((timer) => clearTimeout(timer));
      timers.clear();
    };
  }, [push]);

  return (
    <ToastContext.Provider value={api}>
      {children}
      <div className={styles.container} role="status" aria-live="polite">
        {toasts.map((toast) => (
          <div key={toast.id} className={`${styles.toast} ${styles.variants[toast.variant]}`}>
            <p className={styles.message}>{toast.message}</p>
            <button
              type="button"
              className={styles.closeButton}
              onClick={() => dismiss(toast.id)}
              aria-label="Dismiss notification"
            >
              ✕
            </button>
          </div>
        ))}
      </div>
    </ToastContext.Provider>
  );
}

export function useToast(): ToastApi {
  const ctx = useContext(ToastContext);
  if (!ctx) throw new Error('useToast must be used within ToastProvider');
  return ctx;
}
